import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from "react";

import { cn, flattenStyles, platformProps } from "../../lib";
import type { ImageProps } from "./types";

export type { ImageProps, ImageContentFit, ImageContentPosition, ImageCachePolicy } from "./types";

/** Allow the `--image-*` sizing custom properties on inline `style`. */
type ImageCSSProperties = CSSProperties & Record<`--${string}`, string>;

export const Image = forwardRef<HTMLImageElement, ImageProps>((props, ref) => {
  const {
    src,
    alt,
    contentFit = "cover",
    contentPosition = "center",
    placeholder,
    fallback,
    priority = false,
    loading,
    transition = 200,
    onLoad,
    onError,
    onLoadStart,
    className,
    style,
    accessibilityLabel,
    testID,
  } = props;

  const width = "width" in props ? props.width : undefined;
  const height = "height" in props ? props.height : undefined;
  const fill = "fill" in props ? props.fill : false;
  const aspectRatio = "aspectRatio" in props ? props.aspectRatio : undefined;

  // Which source loaded or failed, cleared while rendering when `src` changes
  // rather than in an effect, so a `load` event that lands before effects run
  // isn't undone, and a source that failed before is tried again.
  const [loadedSrc, setLoadedSrc] = useState<ImageProps["src"] | null>(null);
  const [failedSrc, setFailedSrc] = useState<ImageProps["src"] | null>(null);
  if (loadedSrc !== null && loadedSrc !== src) setLoadedSrc(null);
  if (failedSrc !== null && failedSrc !== src) setFailedSrc(null);
  const hasError = failedSrc === src;
  const isLoading = loadedSrc !== src && !hasError;

  // Once per source, through refs: a parent re-rendering with a new inline
  // callback, or StrictMode replaying effects, doesn't fire it again.
  const onLoadStartRef = useRef(onLoadStart);
  const startedSrcRef = useRef<ImageProps["src"] | null>(null);
  useEffect(() => {
    onLoadStartRef.current = onLoadStart;
  });
  useEffect(() => {
    if (startedSrcRef.current === src) return;
    startedSrcRef.current = src;
    onLoadStartRef.current?.();
  }, [src]);

  const handleLoad = useCallback(() => {
    setLoadedSrc(src);
    onLoad?.();
  }, [onLoad, src]);

  const handleError = useCallback(() => {
    setFailedSrc(src);
    onError?.(new Error("Image failed to load"));
  }, [onError, src]);

  const actualSrc =
    hasError && (typeof fallback === "string" || typeof fallback === "number") ? fallback : src;

  // Web has no blurhash decoder, so only a colour placeholder shows here.
  const placeholderColor = useMemo(() => {
    if (!placeholder || typeof placeholder === "string") return undefined;
    if ("color" in placeholder) return placeholder.color;
    return undefined;
  }, [placeholder]);
  const showsPlaceholder = isLoading && placeholderColor !== undefined;

  // The numeric sizes are defaults a competing `className` size beats, so they
  // can't be inline `width`/`height`, which outrank every class. They ride on
  // custom properties read by `img:where(.class)` rules: element specificity,
  // below any class whatever the stylesheet order, and emitted after
  // preflight's `img { height: auto }`.
  const imageStyle: ImageCSSProperties = {
    // Pinned like native. An absolutely positioned <img> keeps its intrinsic
    // size between insets, so it also needs an explicit 100%.
    ...(fill && {
      position: "absolute",
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      width: "100%",
      height: "100%",
    }),
    ...(style ? flattenStyles([style]) : undefined),
    ...(width !== undefined && { "--image-width": `${width}px` }),
    ...(height !== undefined && { "--image-height": `${height}px` }),
    ...(aspectRatio !== undefined && { "--image-aspect-ratio": `${aspectRatio}` }),
    objectFit: contentFit,
    objectPosition: contentPosition,
    transitionProperty: "opacity, background-color",
    transitionDuration: `${transition}ms`,
    backgroundColor: showsPlaceholder ? placeholderColor : undefined,
  };

  const imageClassName = cn(
    width !== undefined && "[img:where(&)]:w-[--image-width]",
    height !== undefined && "[img:where(&)]:h-[--image-height]",
    aspectRatio !== undefined && "[img:where(&)]:aspect-[--image-aspect-ratio]",
    // The placeholder colour is the element's own background, so it must not be
    // faded out with the not-yet-loaded image.
    isLoading && !showsPlaceholder ? "opacity-0" : "opacity-100",
    className,
  );

  if (
    hasError &&
    fallback !== undefined &&
    typeof fallback !== "string" &&
    typeof fallback !== "number"
  ) {
    return <>{fallback}</>;
  }

  return (
    <img
      ref={ref}
      src={typeof actualSrc === "string" ? actualSrc : undefined}
      alt={alt}
      loading={loading || (priority ? "eager" : "lazy")}
      onLoad={handleLoad}
      onError={handleError}
      className={imageClassName}
      style={imageStyle}
      aria-label={accessibilityLabel}
      {...platformProps({ testID })}
    />
  );
});

Image.displayName = "Image";
